import type {CompleteGeometryData, AbstractGeometryData} from "#template/interfaces/geometry-data"
import type {ISceneManager} from "#template/interfaces/scene-manager"
import type {Inlet, Outlet} from "#template/runtime-graph/slots"
import {NotReady} from "#template/runtime-graph/slots"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import type {NodeClassImpl} from "#template/runtime-graph/types"
import type {Subscription} from "rxjs"
import {defer, map} from "rxjs"

const TD = TypeDescriptors

const generateGeometryDescriptor = {
    sceneManager: TD.inlet(TD.Identity<ISceneManager>()),
    inputGeometryData: TD.inlet(TD.AbstractGeometryData),
    completeGeometryData: TD.outlet(TD.CompleteGeometryData),
}

export class GenerateGeometry implements NodeClassImpl<typeof generateGeometryDescriptor, typeof GenerateGeometry> {
    static descriptor = generateGeometryDescriptor
    static uniqueName = "GenerateGeometry"
    sceneManager!: Inlet<ISceneManager>
    inputGeometryData!: Inlet<AbstractGeometryData>
    completeGeometryData!: Outlet<CompleteGeometryData>

    private pending: Subscription | null = null

    run() {
        this.completeGeometryData.emitIfChanged(NotReady)
        this.pending?.unsubscribe()
        this.pending = null

        const {sceneManager, inputGeometryData} = this

        if (sceneManager === NotReady || inputGeometryData === NotReady) {
            return
        }

        this.pending = sceneManager.addTask({
            description: `evaluateGeometry(${inputGeometryData.displayGeometryGraph.type})`,
            task: defer(() => sceneManager.evaluateGeometry(inputGeometryData.displayGeometryGraph, inputGeometryData.displayGeometryGraphResources)).pipe(
                map((reified) => {
                    this.completeGeometryData.emitIfChanged({
                        reified,
                        abstract: inputGeometryData,
                    })
                }),
            ),
            critical: true,
        })
    }
}
